import React, { useEffect, useState } from 'react';
import { FieldArray, Formik } from 'formik';
import { useI18n } from '@whizz/react-i18n';
import type { TopicFocusEntryInterface, TopicStudentsEntry } from '@whizz/api-model/dist/@types';
import type { OptionTypeBase } from 'react-select';
import type { ToggleModalProps } from '../../../../components/modal/types';
import type { SetTopicFocusType } from '../../../../forms/types';
import { DateRangePicker, Dropdown } from '../../../../components';
import { ModalFooter } from '../../../../components/modal/Modal';
import { CancelButtonModal, ConfirmButtonModal } from '../../../../global';
import SetTopicTable from '../tables/SetTopicTable';
import CustomDateInput from '../CustomDateInput';
import { SET_TOPIC_FOCUS_SCHEMA } from '../../../../forms/validations';
import { GLOBAL_ICONS, PROGRESSIONS_MAX_NUM, Z_INDEXES } from '../../../../util/vars';
import { TODAY } from '../../../../util/dates';
import { DATEPICKER_RANGE } from '../../../../util/data-id';
import { DateTime } from 'luxon';

const after_max_days = 7;

export interface SetTopicModalProps extends Pick<ToggleModalProps, 'toggle'> {
  topicFocusEntries: TopicFocusEntryInterface[];
  topicStudents: TopicStudentsEntry[];
  onSubmit(values: SetTopicFocusType): Promise<void>
}

const SetTopicModal = ({ topicFocusEntries, topicStudents, onSubmit, toggle }: SetTopicModalProps): JSX.Element => {
  const { translate } = useI18n();

  const [ topicOptions, setTopicOptions ] = useState<OptionTypeBase[]>([]);

  useEffect(() => {
    setTopicOptions(topicStudents.map((topic) => {
      return {
        value: topic.getTopicId(),
        label: topic.getTopicName(),
      };
    }));
  }, [ topicStudents.length ]);

  const getAfterOptions = () => {
    const arr: OptionTypeBase[] = [];
    for (let i = 1; i <= after_max_days; i++) {
      arr.push({
        value: i,
        label: `${translate('pages.TOPIC.SET.MODAL.after', { days: i, count: i })}${i === after_max_days ? ` (${translate('pages.TOPIC.SET.MODAL.after_recommended')})` : ''}`,
      });
    }
    return arr;
  };

  return (
    <div>
      <p className={'container mb-lg'}>
        {translate('pages.TOPIC.SET.MODAL.description', {
          progressions: PROGRESSIONS_MAX_NUM,
        })}
      </p>
      <Formik
        initialValues={
          {
            topic: null,
            set_students: [],
            startDate: TODAY,
            endDate: after_max_days,
          } as SetTopicFocusType
        }
        validateOnMount
        validationSchema={SET_TOPIC_FOCUS_SCHEMA}
        onSubmit={(values) => {
          return Promise.resolve(onSubmit(values)).then(() => {
            toggle(false);
          });
        }}
      >
        {({ values, handleSubmit, setFieldValue, isValid, isValidating, isSubmitting }) => (
          <form onSubmit={handleSubmit}>
            <div className={'container'}>
              <div className={'w-full md:w-2/4'}>
                <Dropdown
                  id={'topic'}
                  label={translate('components.SELECTORS.PLACEHOLDERS.TOPIC')}
                  hideLabel
                  buttonPlaceholder={translate('components.SELECTORS.PLACEHOLDERS.TOPIC')}
                  searchNoOptions={translate('components.SELECTORS.NO_OPTIONS.TOPIC')}
                  forceBorder
                  align={'space-between'}
                  options={topicOptions}
                  handleChange={(option: OptionTypeBase) => setFieldValue('topic', option)}
                  value={values.topic}
                />
              </div>
              <div className={'relative'}>
                {!values.topic && (
                  <div
                    className={'absolute inset-0 w-full h-full bg-white bg-opacity-75'}
                    style={{
                      zIndex: Z_INDEXES.level_2 - 10,
                    }}
                  />
                )}
                <div className={'border-t border-b my-md py-md'}>
                  <FieldArray
                    name={'set_students'}
                    render={(arrayHelpers) => (
                      <SetTopicTable
                        topic={values?.topic}
                        topicFocusEntries={topicFocusEntries}
                        selectedTopicStudents={
                          null != values?.topic
                            ? topicStudents.find((item) => item.getTopicId() === values?.topic?.value) ?? null
                            : null
                        }
                        selectedStudentsIds={values.set_students}
                        onSelect={(student) => {
                          if (values.set_students.includes(student)) {
                            arrayHelpers.remove(values.set_students.indexOf(student));
                          } else {
                            arrayHelpers.push(student);
                          }
                        }}
                        onSelectAll={(enabledStudentsIds) => {
                          if (values.set_students.length !== enabledStudentsIds.length) {
                            setFieldValue('set_students', enabledStudentsIds);
                          } else {
                            setFieldValue('set_students', []);
                          }
                        }}
                      />
                    )}
                  />
                </div>
                <div className={'flex items-center justify-between'}>
                  <div className={'flex items-center mr-lg'}>
                    <div className={'font-bold mr-md'}>
                      {translate('pages.TOPIC.SET.MODAL.starts')}
                    </div>
                    <DateRangePicker
                      calendarClassName={DATEPICKER_RANGE}
                      startDate={values.startDate}
                      endDate={null}
                      minDate={TODAY}
                      maxDate={DateTime.now().plus({ years: 1 }).toJSDate() as Date}
                      onChange={(date) => {
                        setFieldValue('startDate', date);
                      }}
                      customInput={<CustomDateInput />}
                    />
                  </div>
                  <div className={'flex flex-1 items-center'}>
                    <div className={'font-bold mr-md'}>
                      {translate('pages.TOPIC.SET.MODAL.ends')}
                    </div>
                    <div className={'flex-1'}>
                      <Dropdown
                        id={'endDate'}
                        label={'End'}
                        hideLabel
                        forceBorder
                        align={'space-between'}
                        disableSearch
                        menuPlacement={'top'}
                        options={getAfterOptions()}
                        handleChange={(option: OptionTypeBase) => {
                          setFieldValue('endDate', option.value);
                        }}
                        value={getAfterOptions().filter((option: OptionTypeBase) => option.value === values.endDate)[0]}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <ModalFooter>
              <CancelButtonModal
                type={'reset'}
                onClick={() => toggle(false)}
                disabled={isSubmitting || isValidating}
              />
              <ConfirmButtonModal
                type={'submit'}
                icon={GLOBAL_ICONS.topic_focus}
                disabled={!isValid || isSubmitting || isValidating}
              >
                {values.set_students.length === 0 ? (
                  translate('pages.TOPIC.SET.MODAL.title')
                ) : (
                  translate('pages.TOPIC.SET.MODAL.confirm', {
                    count: values.set_students.length,
                    students: values.set_students.length,
                  })
                )}
              </ConfirmButtonModal>
            </ModalFooter>
          </form>
        )}
      </Formik>
    </div>
  );
};

SetTopicModal.displayName = 'SetTopicModal';

export default SetTopicModal;
